import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import Textarea from '../UI/Textarea'
import LoadingSpinner from '../UI/LoadingSpinner'
import ConfirmationModal from './ConfirmationModal'
import { X, Mail, Plus, Trash2, User, Shield, Package, CheckCircle, RefreshCw } from 'lucide-react'
import invitationsAPI from '../../services/invitationsAPI'
import { rolesAPI } from '../../services/api'
import { productsAPI } from '../../services/productsAPI'
import api from '../../services/api'
import toast from 'react-hot-toast'

export default function InviteUserModal({ isOpen, onClose, organizationId, onSuccess }) {
  const [invitations, setInvitations] = useState([{
    email: '',
    role_id: '',
    product_ids: [],
    subscription_ids: [],
    // Store selected role per product: { productId: roleId|null }
    product_roles: {}
  }])
  const [roles, setRoles] = useState([])
  const [products, setProducts] = useState([])
  const [productRoles, setProductRoles] = useState({}) // Store roles per product: { productId: [roles] }
  const [refreshingRoles, setRefreshingRoles] = useState({})
  const [isRefreshingAllRoles, setIsRefreshingAllRoles] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [errorTitle, setErrorTitle] = useState('Invitation Failed')
  const queryClient = useQueryClient()

  const { register, handleSubmit, formState: { errors }, reset } = useForm()

  // Fetch organization roles and products
  useEffect(() => {
    if (isOpen && organizationId) {
      const fetchData = async () => {
        try {
          // Fetch organization roles
          const rolesResponse = await rolesAPI.getAll({ user_type: 'organization' })
          setRoles(rolesResponse.data.roles || [])
          
          // Fetch subscribed products for this organization
          const subscriptionsResponse = await api.get(`/subscriptions/organization/${organizationId}`)
          const subscriptions = subscriptionsResponse.data?.data?.subscriptions || []
          
          // Filter only active subscriptions (not expired and status is 'active')
          const activeSubscriptions = subscriptions.filter(sub => {
            // Check if subscription is active (not expired and status is 'active')
            if (sub.status === 'inactive') return false
            if (sub.is_expired === true) return false
            return true
          })
          
          // Extract products from active subscriptions only
          const subscribedProducts = activeSubscriptions.map(sub => ({
            id: sub.product_id,
            subscription_id: sub.id, // ensure we keep subscription id for selected product
            name: sub.product_name,
            description: sub.product_description,
            pricing_model: sub.pricing_model,
            base_price: sub.base_price
          }))
          
          setProducts(subscribedProducts)
        } catch (error) {
          console.error('Error fetching data:', error)
        }
      }
      fetchData()
    }
  }, [isOpen, organizationId])

  // Fetch product roles for a specific product (supports external API)
  const fetchProductRoles = async (productId) => {
    if (!productId) return []

    try {
      const rolesResponse = await productsAPI.getRoles(productId)
      const roles = rolesResponse.data?.data?.roles || rolesResponse.data?.roles || []

      setProductRoles(prev => ({
        ...prev,
        [productId]: roles
      }))
      return roles
    } catch (error) {
      console.error('Error fetching product roles:', error)
      setProductRoles(prev => ({
        ...prev,
        [productId]: []
      }))
      return []
    }
  }

  const pruneStaleProductRoles = (rolesByProductId) => {
    setInvitations((prev) =>
      prev.map((invitation) => {
        const nextProductRoles = { ...(invitation.product_roles || {}) }
        let changed = false
        Object.keys(nextProductRoles).forEach((productId) => {
          const selectedId = nextProductRoles[productId]
          const roles = rolesByProductId[productId]
          if (!selectedId || !roles) return
          if (!roles.some((role) => String(role.id) === String(selectedId))) {
            nextProductRoles[productId] = null
            changed = true
          }
        })
        return changed ? { ...invitation, product_roles: nextProductRoles } : invitation
      })
    )
  }

  const refreshProductRoles = async (productId, { quiet } = {}) => {
    if (!productId) return []
    setRefreshingRoles((prev) => ({ ...prev, [productId]: true }))
    try {
      const roles = await fetchProductRoles(productId)
      pruneStaleProductRoles({ [productId]: roles })
      if (!quiet) {
        toast.success(roles.length ? 'Roles refreshed' : 'No roles found for this product')
      }
      return roles
    } catch (error) {
      console.error('Error refreshing product roles:', error)
      if (!quiet) toast.error('Failed to refresh roles')
      return []
    } finally {
      setRefreshingRoles((prev) => ({ ...prev, [productId]: false }))
    }
  }

  const refreshAllProductRoles = async (productIds) => {
    const ids = [...new Set((productIds || []).filter(Boolean))]
    if (ids.length === 0) return
    setIsRefreshingAllRoles(true)
    try {
      const results = await Promise.all(ids.map(async (id) => [id, await refreshProductRoles(id, { quiet: true })]))
      pruneStaleProductRoles(Object.fromEntries(results))
      toast.success('Roles refreshed')
    } finally {
      setIsRefreshingAllRoles(false)
    }
  }

  // Pre-fetch roles for all subscribed products when modal opens
  useEffect(() => {
    if (products.length > 0 && isOpen) {
      products.forEach(product => {
        if (!productRoles[product.id]) {
          fetchProductRoles(product.id)
        }
      })
    }
  }, [products, isOpen])



  const createInvitationMutation = useMutation(
    (invitationData) => invitationsAPI.create(invitationData),
    {
      onSuccess: () => {
        toast.success('Invitations sent successfully!')
        queryClient.invalidateQueries(['invitations', organizationId])
        onSuccess && onSuccess()
        handleClose()
      },
      onError: (error) => {
        console.error('Error creating invitations:', error)
        const errorMessage = error.response?.data?.message || 'Failed to send invitations'
        
        // Show modal for all errors (not just user limit errors)
        // Check if it's a user limit error - format it better
        if (errorMessage.includes('user limit') || errorMessage.includes('maximum user limit') || errorMessage.includes('reached its maximum')) {
          // Extract product name and user limit from error message for better formatting
          const productMatch = errorMessage.match(/for "([^"]+)"/)
          const limitMatch = errorMessage.match(/(\d+) user/)
          const productName = productMatch ? productMatch[1] : 'this product'
          const userLimit = limitMatch ? limitMatch[1] : ''
          
          // Create a more user-friendly error message
          const formattedMessage = userLimit 
            ? `The subscription plan for "${productName}" has reached its maximum user limit of ${userLimit} user${userLimit === '1' ? '' : 's'}.\n\nYour current plan allows up to ${userLimit} user${userLimit === '1' ? '' : 's'}, and you have already reached this limit. To invite more users, please upgrade your subscription plan.`
            : errorMessage
          
          setErrorTitle('User Limit Reached')
          setErrorMessage(formattedMessage)
        } else {
          setErrorTitle('Invitation Failed')
          setErrorMessage(errorMessage)
        }
        setShowErrorModal(true)
      }
    }
  )

  const addInvitation = () => {
    setInvitations([...invitations, {
      email: '',
      role_id: '',
      product_ids: [],
      subscription_ids: [],
      product_roles: {}
    }])
  }

  const removeInvitation = (index) => {
    const emptyInvitation = {
      email: '',
      role_id: '',
      product_ids: [],
      subscription_ids: [],
      product_roles: {}
    }
    setInvitations(prev => {
      const next = prev.filter((_, i) => i !== index)
      return next.length > 0 ? next : [emptyInvitation]
    })
  }

  const updateInvitation = (index, field, value) => {
    const updated = [...invitations]
    updated[index] = { ...updated[index], [field]: value }
    setInvitations(updated)
  }

  // Handle multiple product selection
  const handleProductSelection = (index, productId, isSelected) => {
    const updated = [...invitations]
    const selectedProductIds = [...(updated[index].product_ids || [])]
    const selectedSubscriptionIds = [...(updated[index].subscription_ids || [])]
    
    const selectedProduct = products.find(p => p.id === productId)
    
    if (isSelected) {
      selectedProductIds.push(productId)
      if (selectedProduct?.subscription_id) {
        selectedSubscriptionIds.push(selectedProduct.subscription_id)
      }
      // Fetch roles for the newly selected product
      fetchProductRoles(productId)
      // Initialize product_roles for this product (null means no role selected yet)
      updated[index].product_roles = {
        ...updated[index].product_roles,
        [productId]: null
      }
    } else {
      const productIndex = selectedProductIds.indexOf(productId)
      if (productIndex > -1) {
        selectedProductIds.splice(productIndex, 1)
        // Remove corresponding subscription_id
        if (selectedProduct?.subscription_id) {
          const subIndex = selectedSubscriptionIds.indexOf(selectedProduct.subscription_id)
          if (subIndex > -1) {
            selectedSubscriptionIds.splice(subIndex, 1)
          }
        }
        // Remove roles for this product
        const newProductRoles = { ...updated[index].product_roles }
        delete newProductRoles[productId]
        updated[index].product_roles = newProductRoles
      }
    }
    
    updated[index].product_ids = selectedProductIds
    updated[index].subscription_ids = selectedSubscriptionIds
    
    setInvitations(updated)
  }

  // Handle role selection (radio) - only one role per product
  const handleRoleSelection = (index, productId, roleId) => {
    const updated = [...invitations]
    const productRolesMap = { ...updated[index].product_roles }

    productRolesMap[productId] = roleId
    updated[index].product_roles = productRolesMap
    setInvitations(updated)
  }


  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      // Filter out empty invitations
      const validInvitations = invitations.filter(inv => inv.email.trim() !== '')
      
      if (validInvitations.length === 0) {
        toast.error('Please add at least one valid email address')
        setIsSubmitting(false)
        return
      }

      // Validate that each invitation has at least one product selected
      for (const invitation of validInvitations) {
        if (!invitation.product_ids || invitation.product_ids.length === 0) {
          toast.error(`Please select at least one product for ${invitation.email}`)
          setIsSubmitting(false)
          return
        }
      }

      // Create a single invitation per email with all selected products
      const promises = []
      for (const invitation of validInvitations) {
        const email = invitation.email.trim()
        
        // Build products_data array with roles per product
        const productsData = invitation.product_ids.map((productId, index) => {
          const subscriptionId = invitation.subscription_ids[index] || null
          const selectedRoleId = invitation.product_roles[productId]
          const selectedRoles = selectedRoleId ? [selectedRoleId] : []
          
          // Find the role name from productRoles state
          const productRoleList = productRoles[productId] || []
          const selectedRole = productRoleList.find(r => r.id === selectedRoleId)
          const roleName = selectedRole ? selectedRole.name : null
          
          return {
            product_id: productId,
            subscription_id: subscriptionId,
            selected_roles: selectedRoles,
            _role_id: selectedRoleId || null,
            _role_name: roleName || null
          }
        })
        
        // Extract role_id from the first product's selected role, or use invitation.role_id if explicitly set
        // Priority: 1) Explicit invitation.role_id (if not empty), 2) First product's role, 3) null
        let roleId = null
        if (invitation.role_id && invitation.role_id.trim() !== '') {
          // Use explicitly set role_id
          roleId = invitation.role_id
        } else if (productsData.length > 0 && productsData[0]._role_id) {
          // Extract from first product's role
          roleId = productsData[0]._role_id
        }
        
        // Create a single invitation with all products
        promises.push(
          createInvitationMutation.mutateAsync({
            organization_id: organizationId,
            email: email,
            role_id: roleId,
            product_ids: invitation.product_ids,
            subscription_ids: invitation.subscription_ids,
            products_data: productsData
          })
        )
      }

      await Promise.all(promises)
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setInvitations([{
        email: '',
        role_id: '',
        product_ids: [],
        subscription_ids: [],
        product_roles: {}
      }])
      setProductRoles({})
      setRefreshingRoles({})
      setIsRefreshingAllRoles(false)
      reset()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Mail className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Invite Organization Users</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Invite Users to Organization
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Send email invitations to users who should have access to this organization. 
                They will receive an email with a link to create their account.
              </p>
            </div>

            <div className="space-y-4">
              {invitations.map((invitation, index) => (
                <Card key={index} className="shadow-none border border-gray-200">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-bold text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          value={invitation.email}
                          onChange={(e) => updateInvitation(index, 'email', e.target.value)}
                          className="w-full"
                          placeholder="user@example.com"
                          disabled={isSubmitting}
                          required
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeInvitation(index)}
                        disabled={isSubmitting}
                        className="text-red-600 hover:text-red-700 whitespace-nowrap mt-7"
                      >
                        Remove
                      </Button>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-bold text-gray-700">
                          Products *
                        </label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => refreshAllProductRoles(products.map((product) => product.id))}
                          disabled={isRefreshingAllRoles || isSubmitting || products.length === 0}
                          title="Refresh product roles"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRefreshingAllRoles ? 'animate-spin' : ''}`} />
                          Refresh roles
                        </Button>
                      </div>
                      {products.length > 0 ? (
                        <div className="space-y-3">
                          {products.map((product) => {
                            const isSelected = invitation.product_ids?.includes(product.id) || false
                            return (
                              <div
                                key={product.id}
                                className={`border rounded-lg transition-all ${
                                  isSelected ? 'border-blue-400 bg-blue-50 shadow-sm' : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className="flex items-start space-x-3 p-3">
                                  <input
                                    type="checkbox"
                                    id={`product-${index}-${product.id}`}
                                    checked={isSelected}
                                    onChange={(e) => handleProductSelection(index, product.id, e.target.checked)}
                                    className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    disabled={isSubmitting}
                                  />
                                  <label
                                    htmlFor={`product-${index}-${product.id}`}
                                    className="flex-1 text-sm text-gray-800 cursor-pointer"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="font-semibold">{product.name}</span>
                                      {productRoles[product.id]?.length && (
                                        <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                          {productRoles[product.id].length} roles
                                        </span>
                                      )}
                                    </div>
                                    {product.description && (
                                      <span className="text-xs text-gray-500 block mt-0.5">
                                        {product.description}
                                      </span>
                                    )}
                                  </label>
                                </div>

                                {isSelected && (
                                  <div className="px-4 pb-4">
                                    <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center justify-between">
                                      <div className="flex items-center space-x-2">
                                        <Shield className="h-3 w-3 text-blue-500" />
                                        <span>Available Roles</span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => refreshProductRoles(product.id)}
                                        disabled={refreshingRoles[product.id] || isSubmitting}
                                        title="Refresh roles"
                                        className="inline-flex items-center text-gray-500 hover:text-blue-600 disabled:opacity-50"
                                      >
                                        <RefreshCw className={`h-3.5 w-3.5 ${refreshingRoles[product.id] ? 'animate-spin' : ''}`} />
                                      </button>
                                    </div>
                                    {refreshingRoles[product.id] ? (
                                      <div className="flex items-center py-2 text-xs text-gray-500">
                                        <LoadingSpinner size="sm" />
                                        <span className="ml-2">Refreshing roles...</span>
                                      </div>
                                    ) : productRoles[product.id]?.length ? (
                                      <div className="space-y-2">
                                        {productRoles[product.id].map((role) => {
                                          const isSelected = invitation.product_roles[product.id] === role.id
                                          return (
                                            <label
                                              key={role.id}
                                              className={`flex items-center justify-between text-sm p-3 rounded-lg border cursor-pointer transition-colors ${
                                                isSelected
                                                  ? 'border-blue-500 bg-white shadow-sm'
                                                  : 'border-gray-200 bg-white hover:border-blue-300'
                                              }`}
                                            >
                                              <div className="flex items-center space-x-2">
                                                <span className={`w-2 h-2 rounded-full ${
                                                  isSelected ? 'bg-blue-600' : 'bg-gray-300'
                                                }`} />
                                                <span className="font-medium text-gray-800">{role.name}</span>
                                              </div>
                                              <input
                                                type="radio"
                                                name={`product-role-${index}-${product.id}`}
                                                value={role.id}
                                                checked={isSelected}
                                                onChange={() => handleRoleSelection(index, product.id, role.id)}
                                                className="text-blue-600 focus:ring-blue-500"
                                                disabled={isSubmitting}
                                              />
                                            </label>
                                          )
                                        })}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500 italic">No roles configured for this product.</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="border border-gray-300 rounded-md p-3 bg-gray-50">
                          <p className="text-sm text-gray-600">
                            No subscribed products available
                          </p>
                        </div>
                      )}
                      {products.length === 0 && (
                        <p className="mt-2 text-sm text-amber-600">
                          This organization doesn't have any subscribed products. Please subscribe to products first.
                        </p>
                      )}
                      {invitation.product_ids?.length > 0 && (
                        <p className="mt-2 text-sm text-gray-600">
                          {invitation.product_ids.length} product{invitation.product_ids.length !== 1 ? 's' : ''} selected
                        </p>
                      )}
                    </div>

                  </CardContent>
                </Card>
              ))}

              <Button
                type="button"
                variant="outline"
                onClick={addInvitation}
                disabled={isSubmitting}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another User
              </Button>
            </div>

            {/* Invitation Info */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Shield className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">Invitation Process</h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Invited users will receive an email with a secure invitation link</li>
                      <li>Invitations expire after 7 days</li>
                      <li>Users can create their account by clicking the invitation link</li>
                      <li>Select one or more subscribed products to assign product-specific roles</li>
                      <li>When multiple products are selected, a single invitation will be created with all selected products</li>
                      <li>You can select roles for each product independently</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[120px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Send Invitations'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Invitation error modal */}
      <ConfirmationModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false)
          setErrorMessage('')
          setErrorTitle('Invitation Failed')
        }}
        onConfirm={() => {
          setShowErrorModal(false)
          setErrorMessage('')
          setErrorTitle('Invitation Failed')
        }}
        title={errorTitle}
        message={errorMessage || 'Failed to send invitation.'}
        confirmText="Got it"
        cancelText={null}
        type="warning"
      />
    </div>
  )
}
